import api from '../../../core/api/client';

// ─── Normalizers ──────────────────────────────────────────────────────────────

const normalizeCompetition = (c) => {
  if (!c || typeof c !== 'object') return c;
  return {
    ...c,
    fundingAmount:        c.funding_amount        ?? c.fundingAmount        ?? 0,
    applicationDeadline:  c.application_deadline  ?? c.applicationDeadline  ?? null,
    eligibilityCriteria:  c.eligibility_criteria  ?? c.eligibilityCriteria  ?? '',
    maxApplications:      c.max_applications      ?? c.maxApplications      ?? null,
    createdBy:            c.created_by            ?? c.createdBy            ?? null,
    createdAt:            c.created_at            ?? c.createdAt            ?? null,
    updatedAt:            c.updated_at            ?? c.updatedAt            ?? null,
  };
};

const normalizeCompetitionList = (payload) => {
  const arr = Array.isArray(payload) ? payload : (payload?.data ?? payload?.results ?? []);
  return arr.map(normalizeCompetition);
};

const serializeCompetition = (data) => {
  if (!data || typeof data !== 'object') return {};
  const {
    fundingAmount, applicationDeadline, eligibilityCriteria, maxApplications,
    createdBy, createdAt, updatedAt,
    ...rest
  } = data;
  const payload = { ...rest };
  if (fundingAmount       !== undefined) payload.funding_amount        = fundingAmount;
  if (applicationDeadline !== undefined) payload.application_deadline  = applicationDeadline;
  if (eligibilityCriteria !== undefined) payload.eligibility_criteria  = eligibilityCriteria;
  if (maxApplications     !== undefined) payload.max_applications      = maxApplications;
  return payload;
};

const normalizeApplication = (a) => {
  if (!a || typeof a !== 'object') return a;
  return {
    ...a,
    competitionId:  a.competition_id  ?? a.competitionId  ?? null,
    projectId:      a.project_id      ?? a.projectId      ?? null,
    submittedBy:    a.submitted_by    ?? a.submittedBy    ?? null,
    submissionData: a.submission_data ?? a.submissionData ?? null,
    createdAt:      a.created_at      ?? a.createdAt      ?? null,
    updatedAt:      a.updated_at      ?? a.updatedAt      ?? null,
  };
};

const normalizeApplicationList = (payload) => {
  const arr = Array.isArray(payload) ? payload : (payload?.data ?? payload?.results ?? []);
  return arr.map(normalizeApplication);
};

const serializeApplication = (data) => {
  if (!data || typeof data !== 'object') return {};
  const {
    competitionId, projectId, submittedBy, submissionData,
    createdAt, updatedAt,
    ...rest
  } = data;
  const payload = { ...rest };
  if (competitionId  !== undefined) payload.competition_id  = competitionId;
  if (projectId      !== undefined) payload.project_id      = projectId;
  if (submittedBy    !== undefined) payload.submitted_by    = submittedBy;
  if (submissionData !== undefined) payload.submission_data = submissionData;
  return payload;
};

const normalizeMetrics = (m) => {
  if (!m || typeof m !== 'object') return m;
  return {
    totalApplications:    m.total_applications    ?? m.totalApplications    ?? 0,
    successRate:          m.success_rate          ?? m.successRate          ?? 0,
    totalFundingReceived: m.total_funding_received ?? m.totalFundingReceived ?? 0,
    activeCompetitions:   m.active_competitions   ?? m.activeCompetitions   ?? 0,
    upcomingDeadlines:    m.upcoming_deadlines    ?? m.upcomingDeadlines    ?? 0,
  };
};

// ─── Service ──────────────────────────────────────────────────────────────────

export const competitionService = {
  // ── Competitions ────────────────────────────────────────────────────────────

  async getAllCompetitions() {
    const response = await api.get('/competitions');
    return normalizeCompetitionList(response.data);
  },

  async getCompetitionById(id) {
    const response = await api.get(`/competitions/${id}`);
    return normalizeCompetition(response.data);
  },

  async createCompetition(data) {
    const response = await api.post('/competitions', serializeCompetition(data));
    return normalizeCompetition(response.data);
  },

  async updateCompetition(id, data) {
    const response = await api.patch(`/competitions/${id}`, serializeCompetition(data));
    return normalizeCompetition(response.data);
  },

  async deleteCompetition(id) {
    const response = await api.delete(`/competitions/${id}`);
    return response.data;
  },

  // ── Metrics ─────────────────────────────────────────────────────────────────

  async getCompetitionMetrics() {
    const response = await api.get('/competitions/metrics');
    return normalizeMetrics(response.data);
  },

  // ── Applications ────────────────────────────────────────────────────────────

  async getAllApplications() {
    const response = await api.get('/applications');
    return normalizeApplicationList(response.data);
  },

  async getApplicationsByCompetition(competitionId) {
    const response = await api.get(`/competitions/${competitionId}/applications`);
    return normalizeApplicationList(response.data);
  },

  async getApplicationById(id) {
    const response = await api.get(`/applications/${id}`);
    return normalizeApplication(response.data);
  },

  async createApplication(data) {
    const response = await api.post('/applications', serializeApplication(data));
    return normalizeApplication(response.data);
  },

  async updateApplication(id, data) {
    const response = await api.patch(`/applications/${id}`, serializeApplication(data));
    return normalizeApplication(response.data);
  },

  async deleteApplication(id) {
    const response = await api.delete(`/applications/${id}`);
    return response.data;
  },
};
